import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Home, ArrowLeft, Search, HelpCircle, AlertTriangle } from "lucide-react";
import VaiuLogo from "@/components/shared/VaiuLogo";
import { i18n } from '@/utils/i18n';

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const commonPaths = [
    { path: '/', label: i18n.__('notFound.nav.home'), icon: Home },
    { path: '/profile', label: i18n.__('notFound.nav.profile'), icon: HelpCircle },
    { path: '/contacts', label: i18n.__('notFound.nav.contacts'), icon: HelpCircle },
    { path: '/transactions', label: i18n.__('notFound.nav.transactions'), icon: HelpCircle },
    { path: '/help', label: i18n.__('notFound.nav.help'), icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6">
        {/* Logo */}
        <div className="text-center">
          <VaiuLogo size="lg" variant="default" className="mx-auto mb-4" />
        </div>

        {/* Main Error Card */}
        <Card className="border-2 border-orange-200 bg-white shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {i18n.__('notFound.pageTitle')}
            </CardTitle>
            <div className="space-y-2">
              <Badge variant="outline" className="text-orange-700 border-orange-300">
                {i18n.__('notFound.error')}
              </Badge>
              <p className="text-gray-600">
                {i18n.__('notFound.message')}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Path Display */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600 mb-1">{i18n.__('notFound.requestedPath')}</p>
              <code className="text-sm font-mono text-gray-800 bg-white px-2 py-1 rounded border">
                {location.pathname}
              </code>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/')}
                className="w-full bg-primary hover:bg-primary/90"
              >
                <Home className="w-4 h-4 mr-2" />
                {i18n.__('notFound.returnHome')}
              </Button>
              
              <Button 
                onClick={() => navigate(-1)}
                variant="outline"
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {i18n.__('notFound.goBack')}
              </Button>
            </div>

            {/* Common Pages */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">{i18n.__('notFound.quickNav')}</p>
              <div className="grid grid-cols-1 gap-2">
                {commonPaths.map((item) => (
                  <Button
                    key={item.path}
                    variant="ghost"
                    onClick={() => navigate(item.path)}
                    className="justify-start text-left h-auto py-2"
                  >
                    <item.icon className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="text-sm">{item.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="text-center">
              <HelpCircle className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-blue-800 mb-3">
                {i18n.__('notFound.help.message')}
              </p>
              <Button 
                onClick={() => navigate('/help')}
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <Search className="w-4 h-4 mr-2" />
                {i18n.__('notFound.help.button')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotFound;
